import { Box } from '@material-ui/core';
import AspectRatioIcon from '@material-ui/icons/AspectRatio';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import Konva from 'konva';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'react';

import { useResponsiveFlags } from '../../../shared/lib';
import { handleTouchMove, handleZoomChange } from '../lib/konvaZoomUtils';
import { SchemaControl } from './schemaControl';
import { SchemaStage } from './schemaStage';

const SCALE_STEP = 0.1;
const SCALE_MIN = 0.4;
const SCALE_MAX = 1.5;

export interface SchemaContainerRef {
  focusTable: (tableId: number) => void;
}

export const SchemaContainer = forwardRef<
  SchemaContainerRef,
  {
    section: GM.ReservesSchemas.SectionDTO;
    selectedTableIds?: number[];
    onClickSchema?: (e: Konva.KonvaEventObject<MouseEvent>) => void;
    onClickTable: (id: number) => void;
    tablesSchedule: GM.ReservesSchemas.TablesSchedule;
    offsetWidth: number;
    onInteractionStart?: () => void;
    onInteractionEnd?: () => void;
    onTableHighlight?: (tableId: number | null) => void;
  }
>(
  (
    {
      section,
      selectedTableIds = [],
      onClickSchema,
      onClickTable,
      tablesSchedule,
      offsetWidth,
      onInteractionStart,
      onInteractionEnd,
      onTableHighlight,
    },
    ref,
  ) => {
    const { isMobile, isMobilePortrait, isMediumScreen } = useResponsiveFlags();
    const schemaContainerRef = useRef<HTMLDivElement>(null);
    const stageRef = useRef<Konva.Stage>(null);
    const [scale, setScale] = useState<number>(1);
    const [isAutoScale, setIsAutoScale] = useState<boolean>(true);

    // Проверка на touch-устройство (мобильное устройство, даже если ширина >900px)
    const isTouchDevice =
      typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
    const [size, setSize] = useState({ width: 300, height: 300 });
    const [bounds, setBounds] = useState({ minX: 0, maxX: 0, minY: 0, maxY: 0 });
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const dragRafIdRef = useRef<number | null>(null);

    const [minScale, setMinScale] = useState<number>(SCALE_MIN);
    const [highlightedTableId, setHighlightedTableId] = useState<number | null>(null);

    const updateSize = useCallback(() => {
      if (!schemaContainerRef.current) {
        return;
      }

      // Используем getBoundingClientRect для получения реальных размеров контейнера
      const rect = schemaContainerRef.current.getBoundingClientRect();

      // Используем высоту самого контейнера, а не window.innerHeight
      // Это гарантирует, что канвас занимает ровно доступную область родителя
      let availableWidth = rect.width;
      const availableHeight = rect.height;

      // Правый отступ только для десктопа (не для планшета и мобильных)
      const containerOffsetRight =
        offsetWidth === 0 ? 0 : offsetWidth >= 300 && !isMediumScreen ? 20 : 0;

      // Вычитаем правый отступ только если он нужен (для десктопа)
      if (containerOffsetRight > 0) {
        availableWidth -= containerOffsetRight;
      }

      // setSize({
      //   width: availableWidth,
      //   height: availableHeight,
      // });

      // Используем requestAnimationFrame для предотвращения ResizeObserver loop
      requestAnimationFrame(() => {
        setSize({
          width: availableWidth,
          height: availableHeight,
        });
      });
    }, [schemaContainerRef, offsetWidth, isMediumScreen]);

    const getContentSize = useCallback(() => {
      let width = 0;
      let height = 0;

      [...(section?.schema?.tables || [])].forEach((t) => {
        width = Math.max(t.x + t.width + 24, width);
        height = Math.max(t.y + t.height + 24, height);
      });

      const elements = [
        ...(section?.schema?.images || []),
        ...(section?.schema?.ellipses || []),
        ...(section?.schema?.marks || []),
        ...(section?.schema?.rectangles || []),
      ];

      elements.forEach((e) => {
        width = Math.max(e.x + e.width, width);
        height = Math.max(e.y + e.height, height);
      });

      return { width, height };
    }, [
      section?.schema?.images,
      section?.schema?.tables,
      section?.schema?.ellipses,
      section?.schema?.marks,
      section?.schema?.rectangles,
    ]);

    useEffect(() => {
      if (!schemaContainerRef.current) {
        return;
      }

      const resizeObserver = new ResizeObserver(updateSize);
      resizeObserver.observe(schemaContainerRef.current);

      // Первоначальный расчет размеров
      updateSize();

      // Также слушаем resize окна на всякий случай
      window.addEventListener('resize', updateSize);

      // Дополнительный вызов через setTimeout для случаев, когда размеры еще не установлены
      const timeoutId = setTimeout(() => {
        updateSize();
      }, 0);

      return () => {
        resizeObserver.disconnect();
        window.removeEventListener('resize', updateSize);
        clearTimeout(timeoutId);
      };
    }, [updateSize, section?.id]);

    useEffect(() => {
      const contentSize = getContentSize();
      const width = contentSize.width * scale;
      const height = contentSize.height * scale;

      const minX = Math.min(0, size.width - width);
      const maxX = 0;
      const minY = Math.min(0, size.height - height);
      const maxY = 0;

      setBounds({ minX, maxX, minY, maxY });
    }, [size.width, size.height, scale, getContentSize]);

    // Публичный API для фокусирования на столе
    useImperativeHandle(
      ref,
      () => ({
        focusTable: (tableId: number) => {
          const table = section?.schema?.tables?.find((t) => t.table.id === tableId);
          if (!table) {
            return;
          }

          const contentSize = getContentSize();
          if (contentSize.width === 0 || contentSize.height === 0) {
            return;
          }

          // Вычисляем центр стола
          const centerX = table.x + table.width / 2;
          const centerY = table.y + table.height / 2;

          // Вычисляем нужную позицию Stage, чтобы стол был в центре видимой области
          const newX = size.width / 2 - centerX * scale;
          const newY = size.height / 2 - centerY * scale;

          // Применяем с учетом bounds
          const boundedX = Math.max(bounds.minX, Math.min(bounds.maxX, newX));
          const boundedY = Math.max(bounds.minY, Math.min(bounds.maxY, newY));

          setPosition({ x: boundedX, y: boundedY });

          // Подсвечиваем стол (подсветка снимется при клике на схему или при следующем focusTable)
          setHighlightedTableId(tableId);
          if (onTableHighlight) {
            onTableHighlight(tableId);
          }
        },
      }),
      [section?.schema?.tables, getContentSize, size, scale, bounds, onTableHighlight],
    );

    // Используем ref для отслеживания предыдущего состояния isAutoScale, чтобы избежать циклов
    const prevIsAutoScaleRef = useRef(isAutoScale);

    useEffect(() => {
      // Обновляем только если isAutoScale действительно изменился с false на true
      const wasAutoScaleEnabled = prevIsAutoScaleRef.current;
      prevIsAutoScaleRef.current = isAutoScale;

      if (isAutoScale && !wasAutoScaleEnabled) {
        // Auto-scale только что включился - плавно возвращаем к исходному размеру
        const contentSize = getContentSize();
        if (contentSize.width === 0 || contentSize.height === 0) {
          return;
        }

        const nextScale = Math.min(
          size.width / contentSize.width,
          size.height / contentSize.height,
        );
        setScale(nextScale);
        setMinScale(Math.min(SCALE_MIN, nextScale));
        // Сбрасываем позицию при включении auto-scale для возврата к исходному виду
        setPosition({ x: 0, y: 0 });
      } else if (isAutoScale && wasAutoScaleEnabled) {
        // Auto-scale уже был включен - обновляем только при изменении размеров контейнера
        const contentSize = getContentSize();
        if (contentSize.width === 0 || contentSize.height === 0) {
          return;
        }

        const nextScale = Math.min(
          size.width / contentSize.width,
          size.height / contentSize.height,
        );
        const scaleDiff = Math.abs(nextScale - scale);

        // Обновляем только если разница значительна (больше 0.01)
        if (scaleDiff > 0.01) {
          setScale(nextScale);
          setMinScale(Math.min(SCALE_MIN, nextScale));
          // Позицию не сбрасываем, если она уже близка к центру
          if (Math.abs(position.x) > 1 || Math.abs(position.y) > 1) {
            setPosition({ x: 0, y: 0 });
          }
        }
      }
    }, [scale, size.width, size.height, isAutoScale, getContentSize, position.x, position.y]);

    useEffect(() => {
      Konva.hitOnDragEnabled = true;
    }, []);

    useEffect(() => {
      const stage = stageRef.current;
      if (!stage) return;

      const unzoom = handleZoomChange(
        stage,
        setScale,
        setPosition,
        {
          minScale,
          maxScale: SCALE_MAX,
        },
        setIsAutoScale,
      );
      const untouch = handleTouchMove(
        stage,
        setScale,
        setPosition,
        {
          minScale,
          maxScale: SCALE_MAX,
        },
        setIsAutoScale,
      );

      return () => {
        unzoom();
        untouch();
      };
    }, [section?.id, minScale]);

    // Детекция взаимодействия для режима "фокус"
    useEffect(() => {
      const stage = stageRef.current;
      if (!stage || !isMobile || (!onInteractionStart && !onInteractionEnd)) return;

      let interactionTimeout: number | null = null;
      let isInteracting = false;

      const handleStart = () => {
        if (!isInteracting && onInteractionStart) {
          isInteracting = true;
          onInteractionStart();
        }
        if (interactionTimeout) {
          clearTimeout(interactionTimeout);
          interactionTimeout = null;
        }
      };

      const handleEnd = () => {
        if (interactionTimeout) {
          clearTimeout(interactionTimeout);
        }
        interactionTimeout = window.setTimeout(() => {
          isInteracting = false;
          if (onInteractionEnd) {
            onInteractionEnd();
          }
          interactionTimeout = null;
        }, 300);
      };

      stage.on('touchstart', handleStart);
      stage.on('mousedown', handleStart);
      stage.on('dragstart', handleStart);
      stage.on('touchend', handleEnd);
      stage.on('touchcancel', handleEnd);
      stage.on('mouseup', handleEnd);
      stage.on('dragend', handleEnd);

      return () => {
        stage.off('touchstart', handleStart);
        stage.off('mousedown', handleStart);
        stage.off('dragstart', handleStart);
        stage.off('touchend', handleEnd);
        stage.off('touchcancel', handleEnd);
        stage.off('mouseup', handleEnd);
        stage.off('dragend', handleEnd);
        if (interactionTimeout) {
          clearTimeout(interactionTimeout);
        }
      };
    }, [section?.id, isMobile, onInteractionStart, onInteractionEnd]);

    // Обработчик двойного тапа для сброса зума в дефолт
    useEffect(() => {
      const stage = stageRef.current;
      if (!stage) return;

      let lastTapTime = 0;
      let tapTimeout: number | null = null;
      const DOUBLE_TAP_DELAY = 300; // Максимальная задержка между тапами в мс

      const handleDoubleTap = () => {
        // Сбрасываем зум в дефолт: включаем auto-scale и сбрасываем позицию
        setIsAutoScale(true);
        setPosition({ x: 0, y: 0 });

        // Вычисляем дефолтный масштаб для умещения
        const contentSize = getContentSize();
        if (contentSize.width > 0 && contentSize.height > 0) {
          const defaultScale = Math.min(
            size.width / contentSize.width,
            size.height / contentSize.height,
          );
          setScale(defaultScale);
        }
      };

      const handleTap = (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
        // Предотвращаем срабатывание двойного тапа при клике на стол
        const target = e.target;
        if (target && target !== stage && target.getParent() !== stage) {
          // Клик был на дочернем элементе (стол и т.д.), не обрабатываем двойной тап
          return;
        }

        const currentTime = Date.now();
        const timeSinceLastTap = currentTime - lastTapTime;

        if (timeSinceLastTap < DOUBLE_TAP_DELAY) {
          // Это двойной тап
          if (tapTimeout) {
            clearTimeout(tapTimeout);
            tapTimeout = null;
          }
          handleDoubleTap();
          lastTapTime = 0;
        } else {
          // Это первый тап, ждем возможного второго
          lastTapTime = currentTime;
          if (tapTimeout) {
            clearTimeout(tapTimeout);
          }
          tapTimeout = window.setTimeout(() => {
            lastTapTime = 0;
            tapTimeout = null;
          }, DOUBLE_TAP_DELAY);
        }
      };

      stage.on('dblclick', handleDoubleTap);
      stage.on('dbltap', handleDoubleTap);
      stage.on('tap', handleTap);
      stage.on('click', handleTap);

      return () => {
        stage.off('dblclick', handleDoubleTap);
        stage.off('dbltap', handleDoubleTap);
        stage.off('tap', handleTap);
        stage.off('click', handleTap);
        if (tapTimeout) {
          clearTimeout(tapTimeout);
        }
      };
    }, [section?.id, getContentSize, size.width, size.height]);

    return (
      <Box display="flex" flexDirection="column" height="100%" style={{ overflow: 'hidden' }}>
        <div
          ref={schemaContainerRef}
          style={{
            flexGrow: 1,
            flexShrink: 1,
            minHeight: 0,
            position: 'relative',
            overflow: 'hidden',
            // CSS оптимизация для GPU-ускорения во время drag
            willChange: isDragging ? 'transform' : 'auto',
          }}
        >
          <SchemaStage
            ref={stageRef}
            size={size}
            contentSize={getContentSize()}
            scale={scale}
            bounds={bounds}
            position={position}
            onSetPosition={setPosition}
            onDragStart={() => {
              setIsDragging(true);
              // Очищаем предыдущий RAF если есть
              if (dragRafIdRef.current !== null) {
                cancelAnimationFrame(dragRafIdRef.current);
                dragRafIdRef.current = null;
              }
            }}
            onDragMove={(e) => {
              // Throttled sync через requestAnimationFrame для оптимизации производительности
              if (dragRafIdRef.current === null) {
                dragRafIdRef.current = requestAnimationFrame(() => {
                  const pos = e.target.position();
                  setPosition({ x: pos.x, y: pos.y });
                  dragRafIdRef.current = null;
                });
              }
            }}
            onDragEnd={() => {
              setIsDragging(false);
              // Отменяем pending RAF
              if (dragRafIdRef.current !== null) {
                cancelAnimationFrame(dragRafIdRef.current);
                dragRafIdRef.current = null;
              }
              // Синхронизация позиции происходит автоматически через onSetPosition в schemaStage.tsx
            }}
            schema={section.schema}
            selectedTableIds={
              highlightedTableId && !selectedTableIds.includes(highlightedTableId)
                ? [...selectedTableIds, highlightedTableId]
                : selectedTableIds
            }
            onClickTable={onClickTable}
            tablesSchedule={tablesSchedule}
            onClickSchema={(e) => {
              // Мгновенно очищаем подсветку стола при клике на схему
              setHighlightedTableId(null);
              if (onTableHighlight) {
                onTableHighlight(null);
              }
              // Вызываем оригинальный обработчик
              if (onClickSchema) {
                onClickSchema(e);
              }
            }}
          />

          {!isMobilePortrait && !isMediumScreen && !isMobile && !isTouchDevice && (
            <Box
              style={{
                position: 'absolute',
                top: 16,
                right: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                background: 'rgba(255, 255, 255, 0.5)',
                borderRadius: 4,
                padding: 8,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.4)',
                zIndex: 1000,
              }}
            >
              <SchemaControl
                icon={<AspectRatioIcon style={{ fontSize: 24 }} />}
                onClick={() => {
                  if (!isAutoScale) {
                    setScale(1);
                    setPosition({ x: 0, y: 0 });
                  }

                  setIsAutoScale((prev) => !prev);
                }}
                isActive={isAutoScale}
                title="Умещать"
              />
              <SchemaControl
                icon={
                  <ZoomInIcon
                    style={{
                      fontSize: 24,
                      color: scale + SCALE_STEP > SCALE_MAX ? 'rgba(0, 0, 0, 0.2)' : 'inherit',
                    }}
                  />
                }
                onClick={() => {
                  setIsAutoScale(false);
                  setScale((prev) => Math.min(prev + SCALE_STEP, SCALE_MAX));
                }}
                title="Увеличить"
                disabled={scale + SCALE_STEP > SCALE_MAX}
              />
              <SchemaControl
                icon={
                  <ZoomOutIcon
                    style={{
                      fontSize: 24,
                      color: scale - SCALE_STEP < minScale ? 'rgba(0, 0, 0, 0.2)' : 'inherit',
                    }}
                  />
                }
                onClick={() => {
                  setIsAutoScale(false);
                  setScale((prev) => Math.max(prev - SCALE_STEP, minScale));
                }}
                title="Отдалить"
                disabled={scale - SCALE_STEP < minScale}
              />
            </Box>
          )}
        </div>
      </Box>
    );
  },
);

SchemaContainer.displayName = 'SchemaContainer';
